import { useCallback, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { TeamAdapter, TeamItemAdapter } from '@makyoapp/makyo-common'
import { AvatarCell, AvatarSize, DeleteModal, SelectDropdown } from '@makyoapp/makyo-ui'

import { useGetOrgTeams } from '@/resolvers/org/query'

export interface DeleteTeamModalProps {
  id?: string
  isLoading?: boolean
  setDeleteTeam: (deleteTeam?: TeamItemAdapter) => void
  deleteTeam: TeamItemAdapter
  onSubmit: (deleteTarget: TeamItemAdapter, transferTarget?: TeamAdapter, done?: () => void) => void
}

/**
 * delete team modal
 *
 * @param id
 * @param isLoading
 * @param currentOrg
 * @param setDeleteTeam
 * @param deleteTeam
 * @param onSubmit
 * @constructor
 */
export function DeleteTeamModal({ id, isLoading, setDeleteTeam, deleteTeam, onSubmit }: DeleteTeamModalProps) {
  const { t } = useTranslation()

  const { data: orgTeams } = useGetOrgTeams()

  const teamOptions = orgTeams?.filter((t) => t.id !== deleteTeam.id) || []

  const [transferTarget, setTransferTarget] = useState<TeamAdapter | undefined>(teamOptions?.[0])

  const resetDelete = useCallback(() => {
    if (isLoading) return
    setDeleteTeam(undefined)
    setTransferTarget(undefined)
  }, [setDeleteTeam, setTransferTarget, isLoading])

  const needTransfer = deleteTeam.apps.length > 0
  return (
    <DeleteModal
      id={id}
      show={true}
      isLoading={isLoading}
      onHide={resetDelete}
      onSubmit={() => {
        onSubmit(deleteTeam, needTransfer && transferTarget ? transferTarget : undefined, resetDelete)
      }}
      title={t('Remove Team')}
      cancelLabel={t('Cancel')}
      canContinue={(needTransfer && transferTarget !== undefined) || !needTransfer}
      continueLabel={!needTransfer ? t('Yes, remove') : t('Transfer apps and remove team')}
      withCancel
    >
      <div className='modal-md max-w-full'>
        <div className='text-sm'>
          {needTransfer ? (
            <>
              <div className='mb-4'>
                <Trans i18nKey='deleteTeamTransferLine1'>
                  There are apps associated with this team. Before this team can be removed, the apps must be
                  transferred to another team within the organization.
                </Trans>
              </div>
              <div className=''>
                <Trans i18nKey='deleteTeamTransferLine2'>
                  Please select a team from the list below, then click the red button to transfer the apps and remove
                  the team. Removed teams can be found in the Trash. Teams can be restored within a 14-day grace period.
                </Trans>
              </div>
            </>
          ) : (
            <Trans i18nKey='deleteTeamConfirm'>
              Are you sure you&apos;d like to remove this team? Team that have been removed can be found in the Trash.
              Teams can be restored within a 14-day grace period.
            </Trans>
          )}
        </div>
      </div>
      {orgTeams && needTransfer && (
        <div className='modal-md mt-4 max-w-full'>
          <SelectDropdown
            value={transferTarget || teamOptions[0]}
            onChange={(st) => {
              setTransferTarget({
                id: st.id,
                name: st.name,
                avatar: st.avatar,
              })
            }}
            optionLabel={(opt) => opt.name}
            renderOption={(team) => {
              return <AvatarCell size={AvatarSize.XS} {...team} />
            }}
            options={teamOptions}
          />
        </div>
      )}
    </DeleteModal>
  )
}
